/**
 * PipelineTracer — Append-only observer for the entity detection pipeline
 *
 * Collects step-by-step trace data during pipeline execution.
 * Dispatches CustomEvent('pipeline-step') for real-time UI updates.
 * Never throws — all operations wrapped in try/catch.
 * Zero overhead when not provided (optional parameter in pipeline).
 */
const PREVIEW_LIMIT = 1000;
export const REDACTED_LLM_BODY_PREVIEW = '[redacted:llm-body]';
export const REDACTED_STEP_PREVIEW = '[redacted:step-preview]';
function truncate(text, limit = PREVIEW_LIMIT) {
    if (text.length <= limit)
        return text;
    return text.slice(0, limit) + `... [${text.length - limit} chars truncated]`;
}
function renderPreview(text, mode, redactedValue) {
    switch (mode) {
        case 'full':
            return text ?? '';
        case 'truncated':
            return truncate(text ?? '');
        case 'redacted':
            return redactedValue;
    }
}
export class PipelineTracer {
    steps = [];
    activeStep = null;
    activeStepStart = 0;
    _startedAt;
    _trigger;
    _modelConfig;
    _previewMode;
    _costSummary = {
        totalUsd: 0,
        pass1Usd: 0,
        pass2Usd: 0,
        auditUsd: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
    };
    _entityDiff = { added: [], updated: [], removed: [], unchanged: 0 };
    constructor(opts) {
        this._startedAt = Date.now();
        this._trigger = opts.trigger;
        this._modelConfig = opts.modelConfig;
        this._previewMode = opts.previewMode ?? 'redacted';
    }
    /**
     * Start tracing a pipeline step. Call endStep() when done.
     */
    startStep(step, opts) {
        try {
            this.activeStepStart = Date.now();
            this.activeStep = {
                step,
                source: opts?.source,
                startedAt: this.activeStepStart,
                input: {
                    itemCount: opts?.inputItemCount,
                    filtered: opts?.filtered,
                    preview: opts?.inputPreview
                        ? renderPreview(opts.inputPreview, this._previewMode, REDACTED_STEP_PREVIEW)
                        : undefined,
                },
            };
            this.emitEvent({
                step,
                source: opts?.source,
                phase: 'start',
                model: step === 'pass1' || step === 'pass2' || step === 'self_audit'
                    ? step === 'pass2'
                        ? this._modelConfig.standard
                        : this._modelConfig.fast
                    : undefined,
            });
        }
        catch (e) {
            console.warn('[PipelineTracer] startStep error:', e);
        }
    }
    /**
     * End the current step with results.
     */
    endStep(opts) {
        try {
            if (!this.activeStep)
                return;
            const now = Date.now();
            const durationMs = now - this.activeStepStart;
            let llmTrace;
            if (opts.llm) {
                llmTrace = {
                    ...opts.llm,
                    promptPreview: renderPreview(opts.llm.promptPreview, this._previewMode, REDACTED_LLM_BODY_PREVIEW),
                    responsePreview: renderPreview(opts.llm.responsePreview, this._previewMode, REDACTED_LLM_BODY_PREVIEW),
                };
                // Accumulate cost
                this._costSummary.totalInputTokens += opts.llm.promptTokens;
                this._costSummary.totalOutputTokens += opts.llm.completionTokens;
                this._costSummary.totalUsd += opts.llm.costUsd;
                if (this.activeStep.step === 'pass1') {
                    this._costSummary.pass1Usd += opts.llm.costUsd;
                }
                else if (this.activeStep.step === 'pass2') {
                    this._costSummary.pass2Usd += opts.llm.costUsd;
                }
                else if (this.activeStep.step === 'self_audit') {
                    this._costSummary.auditUsd += opts.llm.costUsd;
                }
            }
            const completedStep = {
                step: this.activeStep.step,
                source: this.activeStep.source,
                startedAt: this.activeStepStart,
                completedAt: now,
                durationMs,
                status: opts.status ?? 'success',
                error: opts.error,
                input: this.activeStep.input ?? {},
                output: {
                    itemCount: opts.outputItemCount,
                    preview: opts.outputPreview
                        ? renderPreview(opts.outputPreview, this._previewMode, REDACTED_STEP_PREVIEW)
                        : undefined,
                    summary: opts.outputSummary,
                },
                llm: llmTrace,
            };
            this.steps.push(completedStep);
            this.emitEvent({
                step: completedStep.step,
                source: completedStep.source,
                phase: 'end',
                status: completedStep.status,
                durationMs,
                model: llmTrace?.model,
                outputSummary: opts.outputSummary,
                error: opts.error,
            });
            this.activeStep = null;
        }
        catch (e) {
            console.warn('[PipelineTracer] endStep error:', e);
            this.activeStep = null;
        }
    }
    /**
     * Set entity diff for this run.
     */
    setEntityDiff(diff) {
        try {
            this._entityDiff = diff;
        }
        catch (e) {
            console.warn('[PipelineTracer] setEntityDiff error:', e);
        }
    }
    /**
     * Build the final PipelineTrace object.
     */
    finalize(status) {
        const now = Date.now();
        return {
            id: `trace-${this._startedAt}-${Math.random().toString(36).slice(2, 8)}`,
            startedAt: this._startedAt,
            completedAt: now,
            trigger: this._trigger,
            status,
            steps: this.steps,
            costSummary: this._costSummary,
            entityDiff: this._entityDiff,
            modelConfig: this._modelConfig,
        };
    }
    /**
     * Get collected steps so far (for debugging).
     */
    get collectedSteps() {
        return this.steps;
    }
    get costSummary() {
        return this._costSummary;
    }
    emitEvent(detail) {
        try {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (typeof globalThis !== 'undefined' &&
                typeof globalThis.dispatchEvent === 'function') {
                globalThis.dispatchEvent(new CustomEvent('pipeline-step', { detail }));
            }
        }
        catch {
            // No window (SSR / tests) — silently skip
        }
    }
}
//# sourceMappingURL=pipeline-tracer.js.map